import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import dayjs from 'dayjs';

import { DataTable, StatusBadge, EmptyState, ActionMenu } from '../../../components/shared';
import { suppliers } from '../../../services/dataClient';
import { fieldSx } from '../../../theme/fieldStyles';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

const TODAY = dayjs().format('YYYY-MM-DD');
const activeSuppliers = suppliers.filter((s) => s.active);

const PRESETS = [
  { label: 'This Month',    value: 'thisMonth' },
  { label: 'Last Month',    value: 'lastMonth' },
  { label: 'Last 3 Months', value: 'last3'     },
  { label: 'Last 6 Months', value: 'last6'     },
  { label: 'Year to Date',  value: 'ytd'       },
  { label: 'Custom Range',  value: 'custom'    },
];

// Derive effective status: Pending past due → Overdue
const effectiveStatus = (p) =>
  p.status === 'Pending' && p.dueDate < TODAY ? 'Overdue' : p.status;

const EMPTY_FORM = { vendorName: '', poId: '', amount: '', dueDate: '', notes: '' };

// ─── Tab summary bar ──────────────────────────────────────────────────────────
function TabSummaryBar({ payments }) {
  const enriched = payments.map((p) => ({ ...p, _status: effectiveStatus(p) }));
  const stats = [
    { label: 'Total Payments', value: enriched.length },
    { label: 'Pending',  value: enriched.filter((p) => p._status === 'Pending').length  },
    { label: 'Paid',     value: enriched.filter((p) => p._status === 'Paid').length     },
    { label: 'Overdue',  value: enriched.filter((p) => p._status === 'Overdue').length, alert: true },
  ];
  return (
    <Box sx={{ display: 'flex', gap: 3, px: 3, py: 2, borderBottom: '1px solid var(--grey-200)' }}>
      {stats.map((s) => (
        <Box key={s.label} sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: s.alert && s.value > 0 ? '#B71C1C' : 'var(--grey-900)' }}>
            {s.value}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>{s.label}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ─── Record Payment modal ─────────────────────────────────────────────────────
function PaymentModal({ open, allPos, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const handleEnter = () => setForm(EMPTY_FORM);
  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const vendorPos = useMemo(
    () => (form.vendorName ? allPos.filter((p) => p.vendorName === form.vendorName) : []),
    [form.vendorName, allPos],
  );

  const isValid = form.vendorName && parseFloat(form.amount) > 0 && form.dueDate;

  const handleSave = () => {
    const linkedPo = allPos.find((p) => p.id === form.poId);
    onSave({
      vendorName:    form.vendorName,
      amount:        parseFloat(form.amount),
      dueDate:       form.dueDate,
      notes:         form.notes || null,
      poId:          form.poId || null,
      poNumber:      linkedPo?.poNumber ?? null,
      status:        'Pending',
      paidDate:      null,
    });
  };

  return (
    <Dialog open={open} onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Record Payment
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <FormControl size="small" fullWidth sx={fieldSx}>
          <InputLabel>Supplier</InputLabel>
          <Select label="Supplier" value={form.vendorName} onChange={(e) => setForm((prev) => ({ ...prev, vendorName: e.target.value, poId: '' }))}>
            {activeSuppliers.map((s) => (
              <MenuItem key={s.id} value={s.name} sx={{ fontSize: '0.875rem' }}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth sx={fieldSx} disabled={!form.vendorName}>
          <InputLabel>Linked PO (optional)</InputLabel>
          <Select label="Linked PO (optional)" value={form.poId} onChange={set('poId')}>
            <MenuItem value="" sx={{ fontSize: '0.875rem' }}>— None —</MenuItem>
            {vendorPos.map((p) => (
              <MenuItem key={p.id} value={p.id} sx={{ fontSize: '0.875rem' }}>{p.poNumber} — {p.partNumber}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField size="small" label="Amount ($)" type="number" value={form.amount} onChange={set('amount')} fullWidth sx={fieldSx} inputProps={{ min: 0, step: '0.01' }} />
          <TextField size="small" label="Due Date" type="date" value={form.dueDate} onChange={set('dueDate')} fullWidth sx={fieldSx} InputLabelProps={{ shrink: true }} />
        </Box>

        <TextField size="small" label="Notes (optional)" value={form.notes} onChange={set('notes')} multiline rows={2} fullWidth sx={fieldSx} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography onClick={onClose} sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          Cancel
        </Typography>
        <Button variant="contained" size="small" disabled={!isValid} onClick={handleSave}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2 }}>
          Record Payment
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Mark Paid modal ──────────────────────────────────────────────────────────
function MarkPaidModal({ open, payment, onClose, onConfirm }) {
  const [paidDate, setPaidDate] = useState(TODAY);
  const handleEnter = () => setPaidDate(TODAY);
  return (
    <Dialog open={open} onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }} maxWidth="xs" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Mark as Paid
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        {payment && (
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', mb: 2 }}>
            {payment.paymentNumber} — {fmt(payment.amount)} to {payment.vendorName}
          </Typography>
        )}
        <TextField
          size="small" label="Paid Date" type="date" value={paidDate}
          onChange={(e) => setPaidDate(e.target.value)}
          fullWidth InputLabelProps={{ shrink: true }} sx={fieldSx}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography onClick={onClose} sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          Cancel
        </Typography>
        <Button variant="contained" size="small" disabled={!paidDate} onClick={() => onConfirm(paidDate)}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2 }}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────
export default function PaymentsTab({
  payments, allPayments, onUpdate, allPos, onSwitchToPo,
  preset, onPresetChange,
  customStart, onCustomStartChange,
  customEnd, onCustomEndChange,
}) {
  const navigate = useNavigate();
  const [search,        setSearch]        = useState('');
  const [sortConfig,    setSortConfig]    = useState({ field: 'dueDate', direction: 'asc' });
  const [payModalOpen,  setPayModalOpen]  = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);

  const handleSort = (field) => setSortConfig((prev) => ({
    field, direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
  }));

  const enriched = useMemo(
    () => payments.map((p) => ({ ...p, _status: effectiveStatus(p) })),
    [payments],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...enriched]
      .filter((p) =>
        !q ||
        p.paymentNumber.toLowerCase().includes(q) ||
        p.vendorName.toLowerCase().includes(q) ||
        (p.poNumber ?? '').toLowerCase().includes(q),
      )
      .sort((a, b) => {
        const av = a[sortConfig.field] ?? '';
        const bv = b[sortConfig.field] ?? '';
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
  }, [enriched, search, sortConfig]);

  const handleSavePayment = (data) => {
    const nextNum = allPayments.length + 1;
    const newPayment = {
      id: `pay-new-${Date.now()}`,
      paymentNumber: `PAY-${4000 + nextNum}`,
      ...data,
    };
    onUpdate((prev) => [newPayment, ...prev]);
    setPayModalOpen(false);
  };

  const handleMarkPaid = (paidDate) => {
    onUpdate((prev) =>
      prev.map((p) =>
        p.id === markPaidTarget.id ? { ...p, status: 'Paid', paidDate } : p,
      ),
    );
    setMarkPaidTarget(null);
  };

  const columns = [
    {
      field: 'paymentNumber',
      headerName: 'Payment #',
      sortable: true,
      renderCell: (row) => (
        <Typography
          onClick={(e) => { e.stopPropagation(); navigate(`/accounting/payments/${row.id}`); }}
          sx={{ fontSize: '0.875rem', color: '#009688', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.paymentNumber}
        </Typography>
      ),
    },
    { field: 'vendorName', headerName: 'Supplier', sortable: true },
    {
      field: 'amount',
      headerName: 'Amount',
      sortable: true,
      renderCell: (row) => (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{fmt(row.amount)}</Typography>
      ),
    },
    {
      field: '_status',
      headerName: 'Status',
      sortable: true,
      renderCell: (row) => <StatusBadge status={row._status} />,
    },
    {
      field: 'dueDate',
      headerName: 'Due Date',
      sortable: true,
      renderCell: (row) => {
        const overdue = row._status === 'Overdue';
        return (
          <Typography sx={{ fontSize: '0.875rem', color: overdue ? '#B71C1C' : 'inherit', fontWeight: overdue ? 600 : 400 }}>
            {row.dueDate}
          </Typography>
        );
      },
    },
    {
      field: 'paidDate',
      headerName: 'Paid Date',
      sortable: true,
      renderCell: (row) => (
        <Typography sx={{ fontSize: '0.875rem', color: row.paidDate ? 'inherit' : 'var(--grey-400)' }}>
          {row.paidDate ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'poNumber',
      headerName: 'Linked PO #',
      sortable: false,
      renderCell: (row) =>
        row.poNumber ? (
          <Typography
            onClick={(e) => { e.stopPropagation(); onSwitchToPo(row.poNumber); }}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            {row.poNumber}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>
        ),
    },
    {
      field: '_actions',
      headerName: '',
      sortable: false,
      renderCell: (row) => {
        const actions = [];
        if (row._status === 'Pending' || row._status === 'Overdue') {
          actions.push({ label: 'Mark as Paid', onClick: () => setMarkPaidTarget(row) });
        }
        if (row.poId) {
          actions.push({ label: 'View Linked PO', onClick: () => onSwitchToPo(row.poNumber) });
        }
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <ActionMenu actions={actions} hideUntilRowHover />
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <TabSummaryBar payments={payments} />

      {/* Search + filter + record */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}>
        <TextField
          size="small"
          placeholder="Search by Payment #, Supplier, or PO #…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} /></InputAdornment> }}
          sx={{ minWidth: 400, maxWidth: 400, ...fieldSx }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Select
            size="small"
            value={preset ?? 'thisMonth'}
            onChange={(e) => onPresetChange?.(e.target.value)}
            sx={{ minWidth: 155, fontSize: '0.875rem', ...fieldSx['& .MuiOutlinedInput-root'], '& .MuiSelect-select': { paddingTop: '5px', paddingBottom: '5px' } }}
          >
            {PRESETS.map((p) => (
              <MenuItem key={p.value} value={p.value} sx={{ fontSize: '0.875rem' }}>{p.label}</MenuItem>
            ))}
          </Select>
          {preset === 'custom' && (
            <>
              <TextField size="small" type="date" label="From" value={customStart ?? ''}
                onChange={(e) => onCustomStartChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ width: 145, ...fieldSx }} />
              <TextField size="small" type="date" label="To" value={customEnd ?? ''}
                onChange={(e) => onCustomEndChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ width: 145, ...fieldSx }} />
            </>
          )}
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setPayModalOpen(true)}
            sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none' }}>
            Record Payment
          </Button>
        </Box>
      </Box>

      <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <EmptyState title="No payments found" description="Try adjusting your search or date range." />
        ) : (
          <DataTable columns={columns} data={filtered} sortConfig={sortConfig} onSort={handleSort} />
        )}
      </Box>

      <PaymentModal
        open={payModalOpen}
        allPos={allPos}
        onClose={() => setPayModalOpen(false)}
        onSave={handleSavePayment}
      />
      <MarkPaidModal
        open={Boolean(markPaidTarget)}
        payment={markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        onConfirm={handleMarkPaid}
      />
    </>
  );
}
